import { useForm } from 'react-hook-form'
import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import { BpInput } from '../../../../shared/components/forms/input/BpInput'
import { URL_REGEX } from '../../../../shared/consts/RegularExpressions.const'
import { ChangeEvent, FormHTMLAttributes, useEffect } from 'react'
import { BpSelect } from '../../../../shared/components/forms/select/BpSelect'
import { useTranslation } from 'react-i18next'
import { BpTextArea } from '../../../../shared/components/forms/textarea/BpTextArea'
import styles from './CompanySettingsForm.module.scss'
import {
  useGetFilterAssistanceRequestQuery,
  useGetFilterCompanyRoleQuery,
  useGetFilterContactMethodQuery,
} from '../../../../app/api'

export interface ICompanySettingsFormData {
  companyName: string
  companyWebsite: string
  companyRole: string
  assistanceRequest: string[]
  other: string
  contactMethod: string
  contactDetails: string
}

export interface ICompanySettingsFormProps extends FormHTMLAttributes<HTMLFormElement> {
  submitHandler?: (data: ICompanySettingsFormData) => void
  isLoading?: boolean
}

export const CompanySettingsForm = ({
  submitHandler,
  isLoading = false,
  ...formProps
}: ICompanySettingsFormProps) => {
  const { t } = useTranslation()
  const { data: companyRole } = useGetFilterCompanyRoleQuery()
  const { data: assistanceRequest } = useGetFilterAssistanceRequestQuery()
  const { data: contactMethodOptions } = useGetFilterContactMethodQuery()

  const {
    handleSubmit,
    register,
    formState: { errors, isValid },
    setValue,
    watch,
    trigger,
  } = useForm<ICompanySettingsFormData>({
    mode: 'onChange',
    defaultValues: {
      companyName: '',
      companyWebsite: '',
      companyRole: '',
      assistanceRequest: [],
      other: '',
      contactMethod: '',
      contactDetails: '',
    },
  })

  const formValues = watch()

  useEffect(() => {
    if (formValues.contactMethod && formValues.contactDetails) {
      trigger('contactDetails')
    }
  }, [formValues.contactMethod, formValues.contactDetails, trigger])

  return (
    <>
      <h2 className={styles.title}>{t('pages.auth.createYourAccount')}</h2>
      <form
        {...formProps}
        className={styles.form}
        onSubmit={handleSubmit((data: ICompanySettingsFormData) => {
          if (submitHandler) submitHandler(data)
        })}
      >
        <BpInput
          required
          error={errors.companyName?.message}
          fieldSize='lg'
          label={t('pages.auth.companyName')}
          {...register('companyName', {
            required: t('fields.validate.companyNameRequired'),
          })}
        ></BpInput>
        <BpInput
          required
          error={errors.companyWebsite?.message}
          fieldSize='lg'
          label={t('pages.auth.companyWebsite')}
          {...register('companyWebsite', {
            required: t('fields.validate.companyWebsiteRequired'),
            pattern: {
              value: URL_REGEX,
              message: t('fields.validate.invalidWebsiteAddress'),
            },
          })}
        ></BpInput>
        <BpSelect
          hasTranslate
          label={t('pages.auth.companyRole')}
          options={companyRole}
          {...register('companyRole', {
            required: t('fields.validate.required'),
          })}
          required
        />
        <BpSelect
          multiple
          label={t('pages.auth.whatCanHelp')}
          options={assistanceRequest}
          {...register('assistanceRequest', {
            required: t('fields.validate.required'),
          })}
          required
        />
        {formValues.assistanceRequest.includes('other') && (
          <BpTextArea
            error={errors.other?.message}
            label={t('pages.auth.tellYourInterest')}
            {...register('other', {
              onChange: (e) => {
                setValue('other', e.target.value.trimStart())
              },
              required: t('fields.validate.required'),
              minLength: {
                value: 3,
                message: t('fields.validate.leastCountCharacters', {
                  count: 3,
                }),
              },
              maxLength: {
                value: 200,
                message: t('fields.validate.underCountCharacters', {
                  count: 200,
                }),
              },
            })}
            required
          />
        )}
        <BpSelect
          label={t('pages.auth.preferredContactMethod')}
          options={contactMethodOptions}
          {...register('contactMethod')}
        />
        {formValues.contactMethod && (
          <BpInput
            required
            error={errors.contactDetails?.message}
            label={t('pages.auth.contact.' + formValues.contactMethod)}
            {...register('contactDetails', {
              required: t('fields.validate.required'),
              validate: (value) => {
                if (!value) return true

                switch (formValues.contactMethod) {
                  case 'email': {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
                    return emailRegex.test(value) || t('fields.validate.invalidEmail')
                  }

                  case 'linkedin': {
                    const linkedinRegex = /^https?:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$/
                    return linkedinRegex.test(value) || t('fields.validate.invalidLinkedIn')
                  }

                  case 'telegram':
                  case 'skype': {
                    const textRegex = /^[a-zA-Z0-9_@.]+$/
                    return textRegex.test(value) || t('fields.validate.invalidUsername')
                  }

                  case 'whatsapp': {
                    const phoneRegex = /^\+?[1-9]\d{1,14}$/
                    return phoneRegex.test(value) || t('fields.validate.invalidPhone')
                  }

                  default:
                    return true
                }
              },
            })}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setValue('contactDetails', e.target.value)

              if (formValues.contactMethod) {
                void trigger('contactDetails')
              }
            }}
          />
        )}
        <div className={styles.submitWrap}>
          <BpButton
            className={styles.submitButton}
            disabled={!isValid}
            loading={isLoading}
            size='lg'
            type='submit'
          >
            {t('pages.auth.buttons.startTrial')}
          </BpButton>
        </div>
      </form>
    </>
  )
}
